/**
 * Aba "Chuva" da barra lateral: liga a camada, mostra a hora do GSMaP casada
 * com o quadro atual (e o que há nela) e explica o que o satélite estima.
 */
import { CircleQuestionMark, Droplets, ExternalLink, RefreshCw } from 'lucide-react';
import { formatDecimal, formatInt } from '../lib/format';
import { formatRelative, formatTimeBrt } from '../lib/time';
import { Escala } from '../previsao/LegendaFlutuante';
import { shownTimeOf, useTimeStore } from '../state/time';
import { useNow } from '../ui/hooks';
import { Button, Hint, ProgressBar, Section, Slider, Toggle } from '../ui/primitives';
import { BASE_GSMAP, GSMAP_URL, type QuadroGsmap } from './api';
import { PALETA_GSMAP } from './camada';
import { LIMITES_OPACIDADE, useGsmapStore, type ChuvaNoQuadro } from './store';
import { carregarGsmap, fimDoUltimo } from './sync';
import { avisoDeFrescor, quadroParaInstante } from './tempo';

/** Frase da hora casada com o quadro exibido (`fimDoUltimo`: fim da hora mais nova publicada). */
export function fraseDoQuadro(q: QuadroGsmap | null, noQuadro: ChuvaNoQuadro, instante: number | null, fimDoUltimo: number | null = null): string {
  if (!q) {
    if (instante === null) return '—';
    if (fimDoUltimo !== null && instante > fimDoUltimo) {
      return 'A estimativa de chuva deste horário ainda não saiu (a JAXA publica logo depois que a hora termina). Volte um quadro para ver a chuva.';
    }
    return 'Sem estimativa da JAXA para este horário: o mapa fica sem chuva.';
  }
  if (noQuadro.estado === 'carregando' && noQuadro.c === q.c) return 'Carregando a chuva desta hora…';
  if (noQuadro.estado === 'falhou' && noQuadro.c === q.c) return 'A chuva desta hora não carregou.';
  if (q.sem > 0.5) return 'A JAXA não tem estimativa para a maior parte do setor nesta hora.';
  if (q.max < PALETA_GSMAP.paradas[0].v) return 'Nenhuma chuva estimada nesta hora.';
  const area = q.f1 >= 0.001 ? ` · 1 mm/h ou mais em ${formatDecimal(q.f1 * 100, 1)}% da área` : '';
  return `Mais forte: ${formatDecimal(q.max, q.max < 10 ? 1 : 0)} mm/h${area}.`;
}

export function GsmapPanel() {
  const visivel = useGsmapStore((s) => s.visivel);
  const opacidade = useGsmapStore((s) => s.opacidade);
  const situacao = useGsmapStore((s) => s.situacao);
  const indice = useGsmapStore((s) => s.indice);
  const noQuadro = useGsmapStore((s) => s.noQuadro);
  const progresso = useGsmapStore((s) => s.progresso);
  const { setVisivel, setOpacidade } = useGsmapStore.getState();
  const now = useNow(60_000);
  const instante = useTimeStore((s) => shownTimeOf(s));
  const quadros = indice?.quadros ?? [];
  const q = quadroParaInstante(
    quadros,
    quadros.map((x) => x.t),
    instante,
  );
  const ultimo = fimDoUltimo(indice);
  const aviso = avisoDeFrescor(ultimo, now);
  const baixando = progresso.total > 0 && progresso.baixados + progresso.falhas < progresso.total;

  if (!BASE_GSMAP) {
    return (
      <Section title="Chuva por satélite" icon={Droplets}>
        <Hint>A chuva por satélite está desligada nesta versão.</Hint>
      </Section>
    );
  }

  return (
    <div>
      <Section title="Chuva por satélite" icon={Droplets}>
        <Toggle
          label="Chuva estimada pelo GSMaP (JAXA)"
          checked={visivel}
          onChange={setVisivel}
          description="Chuva estimada a partir de vários satélites: a média de cada hora, refeita a cada 30 min e animada junto com a imagem."
        />

        {visivel && aviso && (
          <div className="rounded-lg border border-alerta-400/40 bg-alerta-400/10 px-2.5 py-1.5 text-[11px] leading-snug text-alerta-200">
            {aviso}
          </div>
        )}
        {visivel && situacao.estado === 'error' && !indice && (
          <div className="rounded-lg border border-alerta-400/40 bg-alerta-400/10 px-2.5 py-1.5 text-[11px] leading-snug text-alerta-200">
            Não deu para baixar a lista de horas da chuva: {situacao.erro}
          </div>
        )}

        {visivel && (
          <>
            <div className="space-y-1">
              <div className="text-[11px] text-ink-300">Chuva média na hora (mm/h)</div>
              <Escala paleta={PALETA_GSMAP} />
            </div>
            {indice && (
              <div className="rounded-lg border border-ink-700/70 bg-ink-800/50 px-2.5 py-1.5 text-xs leading-snug text-ink-200">
                {q && (
                  <span className="mr-1 font-medium text-white tabular-nums">
                    {formatTimeBrt(q.ini)}–{formatTimeBrt(q.fim)}
                  </span>
                )}
                {fraseDoQuadro(q, noQuadro, instante, ultimo)}
                {q && instante !== null && instante > q.fim && (
                  <div className="mt-1 text-[11px] text-ink-400">
                    {q === quadros[quadros.length - 1]
                      ? `A hora mais recente publicada; a seguinte sai pouco depois das ${formatTimeBrt(q.fim + 30 * 60_000)}.`
                      : 'A hora desta imagem faltou na JAXA; esta é a mais próxima.'}
                  </div>
                )}
                {q?.p === 's' && (
                  <div className="mt-1 text-[11px] text-ink-400">Só satélite: o ajuste pelos pluviômetros desta hora ainda não saiu.</div>
                )}
              </div>
            )}
            <Slider
              label="Opacidade"
              value={opacidade}
              {...LIMITES_OPACIDADE}
              onChange={setOpacidade}
              format={(v) => `${Math.round(v * 100)}%`}
            />
            {baixando && (
              <div className="space-y-1">
                <ProgressBar value={progresso.total ? progresso.baixados / progresso.total : 0} />
                <div className="text-[11px] text-ink-300 tabular-nums">
                  Baixando as horas: {formatInt(progresso.baixados)} de {formatInt(progresso.total)}
                </div>
              </div>
            )}
            <Hint>Toque num ponto com chuva para ver a taxa estimada ali.</Hint>
          </>
        )}
      </Section>

      <Section title="Dados" icon={RefreshCw} defaultOpen={false} storageKey="gsmap-dados">
        <div className="space-y-1.5 text-xs text-ink-300">
          {situacao.estado === 'loading' && <div>Baixando a lista de horas…</div>}
          {situacao.estado === 'error' && <div className="text-alerta-300">Falha ao atualizar: {situacao.erro}</div>}
          {indice && (
            <div>
              {formatInt(quadros.length)} horas nas últimas 48 h
              {ultimo !== null && (
                <>
                  {' '}
                  · a mais nova terminou às {formatTimeBrt(ultimo)} ({formatRelative(ultimo, now)})
                </>
              )}
            </div>
          )}
          <Button icon={RefreshCw} onClick={() => void carregarGsmap(true)}>
            Atualizar agora
          </Button>
        </div>
      </Section>

      <Section title="O que é" icon={CircleQuestionMark} defaultOpen={false} storageKey="gsmap-oque">
        <div className="space-y-2 text-xs leading-relaxed text-ink-300">
          <p>
            Uma <strong>estimativa</strong> da chuva, não uma medição no chão. Satélites em órbita baixa medem com
            micro-ondas a chuva por dentro da nuvem quando passam; entre uma passagem e outra, o GOES-19 mostra para onde
            as nuvens andam, e o GSMaP, da agência espacial japonesa (JAXA), junta tudo numa grade de cerca de 10 km.
          </p>
          <p>
            Cada mapa é a <strong>chuva média de uma hora</strong>, refeita a cada 30 min. Esta é a versão de tempo real
            (GSMaP_NOW): sai logo que a hora termina, mas a última meia hora é estendida pelo movimento das nuvens, e por
            isso erra mais que a versão que sai 4 h depois. O AmaView usa a versão ajustada por pluviômetros, que corrige
            o viés do satélite sobre a terra.
          </p>
          <p>
            Chuva fraca e chuva de nuvem baixa escapam com facilidade; manchas lisas de chuva fraca aparecem onde só uma
            passagem de satélite viu a região. Onde há radar, ele mede de perto o que o satélite estima de longe.
          </p>
          <p className="text-ink-400">
            Fonte: GSMaP data by Japan Aerospace Exploration Agency (JAXA) — GSMaP_Gauge_NOW. O AmaView guarda as últimas
            48 h.{' '}
            <a href={GSMAP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-rio-400 hover:underline">
              JAXA Realtime Rainfall Watch
              <ExternalLink size={10} />
            </a>
          </p>
        </div>
      </Section>
    </div>
  );
}
